import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Activity, AlertTriangle, Bot, ChevronDown, ChevronRight, Hash, MessageSquare, Paperclip, ThumbsDown, ThumbsUp, User as UserIcon } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Markdown } from '../components/ui/markdown';
import { ResizeHandle } from '../components/ui/resize-handle';
import { RunPhaseHeader, RunTimelineView } from '../components/run/RunActivity';
import { RunCostChip, runUsageOf } from '../components/run/RunCost';
import { chatApi, type ChatActionRow, type ChatSessionRow, type ChatTurnRow } from '../lib/api';
import { useResizablePanel } from '../lib/resizable';
import { useLiveHeadline, useRunStream } from '../lib/run-stream';
import { cn } from '../lib/utils';

const PAGE = 30;
/** How often an open conversation with a turn still being answered is re-read. */
const LIVE_POLL_MS = 3_000;

function personName(p: { name: string | null; email: string } | null | undefined): string {
  return p?.name?.trim() || p?.email || 'Someone';
}

function when(iso: string): string {
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function isLive(status: ChatTurnRow['status'] | null): boolean {
  return status === 'QUEUED' || status === 'PROCESSING';
}

function StatusBadge({ status }: { status: ChatTurnRow['status'] | null }) {
  if (status === 'FAILED') return <Badge variant="danger">failed</Badge>;
  if (status === 'PROCESSING') return <Badge variant="warning">answering</Badge>;
  if (status === 'QUEUED') return <Badge variant="muted">queued</Badge>;
  return null;
}

/**
 * Conversations with the Slack bot.
 *
 * The console half of the chat: what was asked, what the bot answered and from which pages, what it
 * proposed and whether that was approved, what each answer cost, and the asker's 👍/👎. A turn still
 * being answered streams its timeline live — the same run console every other agent renders — and a
 * finished one replays it from the stored events.
 *
 * Yours by default; a system admin can switch to everyone's. The server decides both, so the toggle
 * only appears when it would be honoured.
 */
export default function Chats() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('session');

  const [everyone, setEveryone] = useState(false);
  const [canSeeEveryone, setCanSeeEveryone] = useState(false);
  const [sessions, setSessions] = useState<ChatSessionRow[]>([]);
  const [listEnd, setListEnd] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const listBusy = useRef(false);

  const [detail, setDetail] = useState<{ session: ChatSessionRow; turns: ChatTurnRow[] } | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  const loadSessions = useCallback(
    async (options: { reset?: boolean } = {}) => {
      if (listBusy.current) return;
      listBusy.current = true;
      try {
        const cursor = options.reset ? undefined : sessions[sessions.length - 1]?.id;
        const page = await chatApi.sessions({ everyone, limit: PAGE, ...(cursor ? { cursor } : {}) });
        setCanSeeEveryone(page.canSeeEveryone);
        setSessions((prev) => (options.reset ? page.sessions : [...prev, ...page.sessions]));
        setListEnd(page.sessions.length < PAGE);
        setListError(null);
      } catch (err) {
        setListError(err instanceof Error ? err.message : 'Could not load conversations');
        setListEnd(true);
      } finally {
        listBusy.current = false;
      }
    },
    [everyone, sessions],
  );

  // Reset whenever the lens changes — "mine" and "everyone's" are different lists, not a filter.
  useEffect(() => {
    setSessions([]);
    setListEnd(false);
    void loadSessions({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [everyone]);

  const loadDetail = useCallback(async (sessionId: string) => {
    try {
      const next = await chatApi.session(sessionId);
      setDetail(next);
      setDetailError(null);
    } catch (err) {
      setDetail(null);
      setDetailError(err instanceof Error ? err.message : 'Could not load this conversation');
    }
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    void loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  // While any turn in the open conversation is still being answered, re-read it: a new turn, an
  // answer landing, a card approved — none of it arrives on the per-turn stream, which is about
  // the agent's work rather than the row.
  const anyLive = detail?.turns.some((t) => isLive(t.status)) ?? false;
  useEffect(() => {
    if (!selectedId || !anyLive) return;
    const timer = setInterval(() => void loadDetail(selectedId), LIVE_POLL_MS);
    return () => clearInterval(timer);
  }, [selectedId, anyLive, loadDetail]);

  const onListScroll = (e: React.UIEvent<HTMLElement>) => {
    const el = e.currentTarget;
    if (!listEnd && el.scrollTop + el.clientHeight >= el.scrollHeight - 120) void loadSessions();
  };

  const select = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('session', id);
    setSearchParams(next, { replace: true });
  };

  const panel = useResizablePanel({ initial: 340, min: 260, max: 520, side: 'left', storageKey: 'chats.list.width' });

  return (
    <div className="flex h-screen overflow-hidden bg-surface">
      <div className="pl-[var(--sidebar-w)] pr-[var(--rail-w)] flex-1 h-full flex overflow-hidden">
        <aside
          onScroll={onListScroll}
          style={{ width: panel.width }}
          className="flex-shrink-0 h-full overflow-y-auto border-r border-border bg-surface-subtle/40"
        >
          <div className="px-4 py-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] font-semibold text-ink-faint uppercase tracking-[0.07em]">Conversations</p>
              {canSeeEveryone && (
                <div className="flex rounded-full border border-border bg-card p-0.5">
                  {([['mine', false], ['everyone', true]] as const).map(([label, value]) => (
                    <button
                      key={label}
                      onClick={() => setEveryone(value)}
                      className={cn(
                        'px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors',
                        everyone === value ? 'bg-orange-500 text-white' : 'text-ink-muted hover:text-ink',
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {listError && <p className="text-[11px] text-danger-text mb-2">{listError}</p>}

            {sessions.length === 0 ? (
              <p className="text-[11px] text-ink-faint">
                {listEnd ? 'No conversations yet. Message the bot in Slack, or @mention it in a channel.' : 'Loading…'}
              </p>
            ) : (
              <div className="space-y-1.5">
                {sessions.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => select(s.id)}
                    className={cn(
                      'w-full px-3 py-2 rounded-md border text-left transition-colors bg-card',
                      selectedId === s.id ? 'border-orange-400' : 'border-border hover:border-orange-300',
                    )}
                  >
                    <span className="flex items-center gap-1.5 text-[10px] text-ink-faint mb-1">
                      {s.isDirect ? <MessageSquare size={10} /> : <Hash size={10} />}
                      <span className="truncate">
                        {s.isDirect ? 'DM' : s.channelId} · {s.askers.map(personName).join(', ') || 'unknown'}
                      </span>
                    </span>
                    <span className="block text-xs text-ink-secondary line-clamp-2">{s.firstQuestion ?? '—'}</span>
                    <span className="mt-1.5 flex items-center gap-2">
                      <StatusBadge status={s.lastStatus} />
                      <span className="text-[10px] text-ink-faint">
                        {s.turnCount} turn{s.turnCount === 1 ? '' : 's'}
                      </span>
                      {s.feedback.up > 0 && (
                        <span className="flex items-center gap-0.5 text-[10px] text-success-text">
                          <ThumbsUp size={10} />{s.feedback.up}
                        </span>
                      )}
                      {s.feedback.down > 0 && (
                        <span className="flex items-center gap-0.5 text-[10px] text-danger-text">
                          <ThumbsDown size={10} />{s.feedback.down}
                        </span>
                      )}
                      <span className="flex-1" />
                      <span className="text-[10px] text-ink-faint">{when(s.lastTurnAt)}</span>
                    </span>
                  </button>
                ))}
                <p className="pt-2 pb-1 text-center text-[10px] text-ink-faint">
                  {listEnd ? 'End of conversations' : 'Scroll for older…'}
                </p>
              </div>
            )}
          </div>
        </aside>

        <ResizeHandle handleProps={panel.handleProps} onDoubleClick={panel.reset} />

        <div className="flex-1 h-full overflow-y-auto min-w-0">
          <div className="px-7 py-7">
            {!selectedId ? (
              <div>
                <h1 className="text-lg font-bold text-ink">Slack chats</h1>
                <p className="text-xs text-ink-muted mt-1 max-w-2xl">
                  Every conversation with the memory's Slack bot: what was asked, how it answered and from which pages,
                  what it proposed to save or set up and whether that was approved, what each answer cost, and the
                  asker's 👍 / 👎. Pick a conversation to read it; one still being answered streams live.
                </p>
              </div>
            ) : detailError ? (
              <p className="text-xs text-danger-text bg-danger-bg border border-danger-ring rounded px-3 py-2">{detailError}</p>
            ) : !detail ? (
              <p className="text-xs text-ink-faint">Loading…</p>
            ) : (
              <Conversation session={detail.session} turns={detail.turns} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Conversation({ session, turns }: { session: ChatSessionRow; turns: ChatTurnRow[] }) {
  const total = turns.reduce(
    (sum, t) => ({ ...sum, costUsd: sum.costUsd + t.costUsd, modelCalls: sum.modelCalls + t.modelCalls }),
    { costUsd: 0, modelCalls: 0 },
  );
  return (
    <div>
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-base font-bold text-ink flex items-center gap-2">
            {session.isDirect ? <MessageSquare size={15} /> : <Hash size={15} />}
            {session.isDirect ? 'Direct message' : `Channel thread in ${session.channelId}`}
          </h1>
          <p className="text-[11px] text-ink-muted mt-1">
            {turns.length} turn{turns.length === 1 ? '' : 's'} · started {new Date(session.createdAt).toLocaleString()}
            {session.project ? ` · current project ${session.project.name}` : ''}
            {total.modelCalls > 0 ? ` · $${total.costUsd.toFixed(3)} in total` : ''}
          </p>
        </div>
      </div>
      <div className="space-y-4">
        {turns.map((turn) => (
          <Turn key={turn.id} turn={turn} />
        ))}
      </div>
    </div>
  );
}

function Turn({ turn }: { turn: ChatTurnRow }) {
  const live = isLive(turn.status);
  // Open by default while it is being answered — that is when the timeline is the whole story.
  const [open, setOpen] = useState(live);
  useEffect(() => {
    if (live) setOpen(true);
  }, [live]);

  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden">
      <div className="px-4 py-2.5 border-b border-border-subtle bg-surface-subtle flex items-center gap-2 flex-wrap">
        <UserIcon size={12} className="text-ink-faint" />
        <span className="text-[11px] font-medium text-ink">{personName(turn.user)}</span>
        <span className="text-[10px] text-ink-faint">{new Date(turn.queuedAt).toLocaleString()}</span>
        <StatusBadge status={turn.status} />
        <span className="flex-1" />
        {turn.feedback === 'UP' && (
          <span className="flex items-center gap-1 text-[10px] text-success-text"><ThumbsUp size={11} />helpful</span>
        )}
        {turn.feedback === 'DOWN' && (
          <span className="flex items-center gap-1 text-[10px] text-danger-text"><ThumbsDown size={11} />not quite</span>
        )}
        {turn.modelCalls > 0 && <RunCostChip usage={runUsageOf(turn)} />}
      </div>

      <div className="px-4 pt-3 pb-1">
        {turn.question ? (
          <p className="text-sm text-ink whitespace-pre-wrap">{turn.question}</p>
        ) : (
          <p className="text-xs text-ink-faint italic">Sent an attachment with no message.</p>
        )}
        <Attachments docs={turn.documents} />
      </div>

      <div className="px-4 py-3">
        <div className="flex items-start gap-2">
          <Bot size={14} className="text-orange-500 mt-0.5 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            {turn.answer ? (
              <Markdown>{turn.answer}</Markdown>
            ) : turn.status === 'FAILED' ? (
              <p className="text-xs text-danger-text">{turn.errorMessage ?? 'Failed'}</p>
            ) : (
              <p className="text-xs text-ink-faint italic">{turn.activity ?? 'Waiting to be answered…'}</p>
            )}
          </div>
        </div>

        {turn.feedbackNote && (
          <p className="mt-3 text-[11px] text-ink-secondary border-l-2 border-danger-ring pl-2">
            <span className="font-medium">Asker's note:</span> {turn.feedbackNote}
          </p>
        )}

        {turn.actions.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {turn.actions.map((action) => (
              <ActionRow key={action.id} action={action} />
            ))}
          </div>
        )}
      </div>

      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-1.5 px-4 py-2 border-t border-border-subtle text-[11px] text-ink-muted hover:text-ink hover:bg-surface-subtle transition-colors"
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <Activity size={11} />
        {live ? 'Live activity' : 'How it answered'}
      </button>
      {open && <TurnTimeline turn={turn} />}
    </div>
  );
}

/** One turn's timeline — streamed while it runs, replayed from the stored events afterwards. */
function TurnTimeline({ turn }: { turn: ChatTurnRow }) {
  // The project argument is ignored for chat turns (see RUN_ENDPOINTS); the hook only needs it set.
  const stream = useRunStream('chat', turn.id, turn.status, 'chat');
  const headline = useLiveHeadline(stream.timeline);
  return (
    <div className="px-4 pb-4 pt-1 space-y-3 border-t border-border-subtle">
      <RunPhaseHeader timeline={stream.timeline} headline={headline} live={stream.live} />
      <RunTimelineView timeline={stream.timeline} live={stream.live} loading={stream.loading} />
      {stream.error && <p className="text-[11px] text-ink-faint">{stream.error}</p>}
    </div>
  );
}

function ActionRow({ action }: { action: ChatActionRow }) {
  const p = action.payload as {
    title?: string;
    projectName?: string;
    automation?: { name?: string };
  };
  const label =
    action.kind === 'INGEST'
      ? `Ingest “${p.title ?? 'untitled'}” ${p.projectName ? `→ ${p.projectName}` : ''}`
      : `Create automation “${p.automation?.name ?? 'untitled'}” in ${p.projectName ?? '?'}`;
  const badge =
    action.status === 'APPROVED' ? (
      <Badge variant="success">approved</Badge>
    ) : action.status === 'CANCELLED' ? (
      <Badge variant="muted">cancelled</Badge>
    ) : action.status === 'FAILED' ? (
      <Badge variant="danger">failed</Badge>
    ) : (
      <Badge variant="info">awaiting approval</Badge>
    );
  const link =
    action.status === 'APPROVED' && action.resultRef
      ? action.kind === 'INGEST'
        ? `/activity?run=${encodeURIComponent(action.resultRef)}`
        : `/automations?automation=${encodeURIComponent(action.resultRef)}`
      : null;

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-border-subtle bg-surface-subtle/60">
      <span className="text-[11px] text-ink-secondary flex-1 min-w-0 truncate">{label}</span>
      {badge}
      {link && (
        <a href={link} className="text-[10px] text-orange-600 hover:underline flex-shrink-0">
          {action.kind === 'INGEST' ? 'view run' : 'open'}
        </a>
      )}
    </div>
  );
}

const SKIP_LABEL: Record<string, string> = {
  'slack-file-unreadable': 'unreadable upload',
  'slack-file-error': 'upload failed to download',
  'drive-no-access': 'Drive link not shared with the service account',
  'drive-not-found': 'Drive file not found',
  'drive-too-large': 'Drive file too large',
  'drive-unsupported': 'Drive file type not readable',
  'drive-empty': 'Drive file had no text',
  'drive-error': 'Drive link failed to read',
};

/** What came with the message: each document that was read, and each that could not be. */
function Attachments({ docs }: { docs: ChatTurnRow['documents'] }) {
  if (!docs || (docs.documents.length === 0 && Object.keys(docs.skipped).length === 0)) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {docs.documents.map((d, i) => (
        <span
          key={`${d.source}-${i}`}
          title={d.source}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-border bg-surface-subtle text-[10px] text-ink-secondary"
        >
          <Paperclip size={10} />
          {d.title}
          <span className="text-ink-faint">· {d.chars.toLocaleString()} chars</span>
        </span>
      ))}
      {Object.entries(docs.skipped).map(([reason, n]) => (
        <span
          key={reason}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-warning-ring bg-warning-bg text-[10px] text-warning-text"
        >
          <AlertTriangle size={10} />
          {n > 1 ? `${n} × ` : ''}
          {SKIP_LABEL[reason] ?? reason}
        </span>
      ))}
    </div>
  );
}
